import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './HarmonogramComponent.css'

const PROGRAM_COLORS = {
  combatives: '#9ecbff',
  reflex: '#9effac',
  kidsSmall: '#FFFF00',
  kidsYoung: '#fff99e',
  kidsJunior: '#ffd99e',
  kidsBlackBelt: '#FFA500',
  master: '#f29eff',
  women: '#FFBCD9',
}

const PROGRAM_LOGOS = {
  'adults-beginers': {
    src: '/images/Gracie-Combatives-Lockup-Logo-COLOR-1200x273.png',
    alt: 'Gracie Combatives',
  },
  'adults-pro': {
    src: '/images/Master-Cycle-Lockup-Logo-COLOR.png',
    alt: 'Master Cycle',
  },
  kids: {
    src: '/images/Gracie-Bullyproof-Lockup-Logo-COLOR.png',
    alt: 'Gracie Bullyproof',
  },
  we: {
    src: '/images/we-logo.png',
    alt: 'Women Empowered',
  },
}

const currentWeekdayIndex = () => {
  const day = new Date().getDay()

  if (day >= 1 && day <= 5) {
    return day - 1
  }

  return 0
}

const emptySlot = (category) => ({
  category,
  empty: true,
})

const isSummerScheduleActive = (date = new Date()) => {
  const year = date.getFullYear()
  const summerScheduleStart = new Date(year, 5, 25)
  const summerScheduleEnd = new Date(year, 7, 21)

  return date >= summerScheduleStart && date < summerScheduleEnd
}

const NEW_SCHEDULE_START = new Date(2026, 7, 21)

const isUpcomingSession = (session) =>
  session.startsWithNewSchedule && new Date() < NEW_SCHEDULE_START

const SUMMER_PAUSED_KIDS_GROUPS = ['Malí šampióni', 'Mladí zápasníci', 'Black Belt Club']

const isSummerPausedSession = (session) =>
  session.category === 'kids' && SUMMER_PAUSED_KIDS_GROUPS.includes(session.subtitle)

const TIMELINE_PIXELS_PER_MINUTE = 2.4
const TIMELINE_STEP_MINUTES = 30
const TIMELINE_SEGMENTS = [
  { key: 'morning', label: 'Ráno', start: 7 * 60, end: 8 * 60 },
  { key: 'afternoon', label: 'Popoludnie', start: 16 * 60, end: 20 * 60 + 30 },
]

const clockTimeToMinutes = (clockTime) => {
  const [hours, minutes] = clockTime.split(':').map(Number)

  return hours * 60 + minutes
}

const getSessionTiming = (session) => {
  const timedSession = session.splitSessions ? session.splitSessions[0] : session

  if (!timedSession || !timedSession.time) {
    return null
  }

  const [startTime, endTime] = timedSession.time.split(' - ')
  const start = clockTimeToMinutes(startTime)
  const end = clockTimeToMinutes(endTime)

  return {
    start,
    end,
    duration: end - start,
  }
}

const minutesToClockTime = (minutes) => {
  const hours = Math.floor(minutes / 60)
    .toString()
    .padStart(2, '0')
  const remainingMinutes = (minutes % 60).toString().padStart(2, '0')

  return `${hours}:${remainingMinutes}`
}

const getTimelineMarks = (segment) => {
  const marks = []

  for (let minutes = segment.start; minutes <= segment.end; minutes += TIMELINE_STEP_MINUTES) {
    marks.push(minutes)
  }

  return marks
}

const getScheduleDays = (womenEmpoweredLabel) => [
  {
    name: 'Pondelok',
    shortName: 'Po',
    sessions: [
      {
        category: 'adults-beginers',
        color: PROGRAM_COLORS.combatives,
        title: 'Gracie Combatives',
        registered: true,
        subtitle: 'Začiatočnícky program',
        age: '13-99 rokov',
        time: '07:00 - 08:00',
      },
      emptySlot('adults-beginers'),
      {
        category: 'kids',
        color: PROGRAM_COLORS.kidsYoung,
        title: 'Gracie Bullyproof',
        registered: true,
        subtitle: 'Malí šampióni',
        age: '5-8 rokov',
        time: '16:00 - 16:50',
      },
      {
        category: 'kids',
        color: PROGRAM_COLORS.kidsJunior,
        title: 'Gracie Bullyproof',
        registered: true,
        subtitle: 'Mladí zápasníci',
        age: '8-12 rokov',
        time: '17:00 - 17:50',
      },
      {
        category: 'adults-beginers',
        color: PROGRAM_COLORS.combatives,
        title: 'Gracie Combatives',
        registered: true,
        subtitle: 'Začiatočnícky program',
        age: '13-99 rokov',
        time: '18:00 - 19:00',
      },
      {
        category: 'adults-pro',
        color: PROGRAM_COLORS.master,
        title: 'Master Cycle',
        registered: true,
        subtitle: 'Program pre pokročilých',
        trainingType: 'GI',
        time: '19:00 - 20:00',
      },
      {
        category: 'adults-pro',
        color: PROGRAM_COLORS.master,
        title: 'Drilovanie techník pre pokročilých',
        time: '20:00 - 20:30',
      },
    ],
  },
  {
    name: 'Utorok',
    shortName: 'Ut',
    sessions: [
      {
        category: 'adults-beginers',
        color: PROGRAM_COLORS.reflex,
        title: 'Gracie Combatives',
        registered: true,
        subtitle: 'Reflex development',
        age: '13-99 rokov',
        time: '07:00 - 08:00',
        startsWithNewSchedule: true,
      },
      emptySlot('adults-beginers'),
      emptySlot('kids'),
      {
        category: 'kids',
        color: PROGRAM_COLORS.kidsBlackBelt,
        title: 'Gracie Bullyproof',
        registered: true,
        subtitle: 'Black Belt Club',
        age: 'iba na pozvánku',
        time: '17:00 - 18:00',
      },
      {
        category: 'we',
        color: PROGRAM_COLORS.women,
        title: 'Women Empowered',
        registered: true,
        subtitle: womenEmpoweredLabel,
        age: '13-99 rokov',
        time: '18:00 - 19:00',
      },
      {
        category: 'adults-beginers',
        color: PROGRAM_COLORS.combatives,
        title: 'Gracie Combatives',
        registered: true,
        subtitle: 'Začiatočnícky program',
        age: '13-99 rokov',
        time: '19:00 - 20:00',
      },
      emptySlot('adults-pro'),
    ],
  },
  {
    name: 'Streda',
    shortName: 'St',
    sessions: [
      {
        category: 'adults-beginers',
        color: PROGRAM_COLORS.combatives,
        title: 'Gracie Combatives',
        registered: true,
        subtitle: 'Začiatočnícky program',
        age: '13-99 rokov',
        time: '07:00 - 08:00',
      },
      emptySlot('adults-beginers'),
      {
        category: 'kids',
        color: PROGRAM_COLORS.kidsYoung,
        title: 'Gracie Bullyproof',
        registered: true,
        subtitle: 'Malí šampióni',
        age: '5-8 rokov',
        time: '16:00 - 16:50',
      },
      {
        category: 'kids',
        color: PROGRAM_COLORS.kidsJunior,
        title: 'Gracie Bullyproof',
        registered: true,
        subtitle: 'Mladí zápasníci',
        age: '8-12 rokov',
        time: '17:00 - 17:50',
      },
      {
        category: 'adults-beginers',
        splitSessions: [
          {
            category: 'adults-beginers',
            color: PROGRAM_COLORS.combatives,
            title: 'Gracie Combatives',
            registered: true,
            subtitle: 'Začiatočnícky program',
            age: '13-99 rokov',
            time: '18:00 - 19:00',
            startsWithNewSchedule: true,
          },
          {
            category: 'adults-beginers',
            color: PROGRAM_COLORS.reflex,
            title: 'Gracie Combatives',
            registered: true,
            subtitle: 'Reflex development',
            age: '13-99 rokov',
            time: '18:00 - 19:00',
          },
        ],
      },
      {
        category: 'adults-pro',
        color: PROGRAM_COLORS.master,
        title: 'Master Cycle',
        registered: true,
        subtitle: 'Program pre pokročilých',
        trainingType: 'NoGi',
        time: '19:00 - 20:00',
      },
      {
        category: 'adults-pro',
        color: PROGRAM_COLORS.master,
        title: 'Drilovanie techník pre pokročilých',
        time: '20:00 - 20:30',
      },
    ],
  },
  {
    name: 'Štvrtok',
    shortName: 'Št',
    sessions: [
      emptySlot('adults-beginers'),
      emptySlot('adults-beginers'),
      emptySlot('kids'),
      {
        category: 'kids',
        color: PROGRAM_COLORS.kidsSmall,
        title: 'Gracie Bullyproof',
        registered: true,
        subtitle: 'Jiu jitsu plyšáci',
        age: '3-5 rokov',
        time: '17:00 - 17:30',
      },
      {
        category: 'adults-pro',
        color: PROGRAM_COLORS.master,
        title: 'Master Cycle',
        registered: true,
        subtitle: 'Program pre pokročilých',
        trainingType: 'FightSim',
        time: '17:40 - 18:40',
      },
      {
        category: 'adults-pro',
        color: PROGRAM_COLORS.master,
        title: 'Drilovanie techník pre pokročilých',
        time: '18:40 - 19:10',
      },
      {
        category: 'adults-beginers',
        color: PROGRAM_COLORS.combatives,
        title: 'Gracie Combatives',
        registered: true,
        subtitle: 'Začiatočnícky program',
        age: '13-99 rokov',
        time: '19:15 - 20:15',
      },
    ],
  },
  {
    name: 'Piatok',
    shortName: 'Pi',
    sessions: [
      emptySlot('adults-beginers'),
      emptySlot('adults-beginers'),
      emptySlot('kids'),
      {
        category: 'kids',
        color: PROGRAM_COLORS.kidsBlackBelt,
        title: 'Gracie Bullyproof',
        registered: true,
        subtitle: 'Black Belt Club',
        age: 'iba na pozvánku',
        time: '17:00 - 18:00',
      },
      {
        category: 'we',
        color: PROGRAM_COLORS.women,
        title: 'Women Empowered',
        registered: true,
        subtitle: womenEmpoweredLabel,
        age: '13-99 rokov',
        time: '18:00 - 19:00',
      },
      {
        category: 'adults-pro',
        color: PROGRAM_COLORS.master,
        title: 'Master Cycle LAB',
        subtitle: 'Program pre pokročilých',
        time: '19:00 - 20:00',
        startsWithNewSchedule: true,
      },
      emptySlot('adults-pro'),
    ],
  },
]

const SessionContent = ({ session, hideTime = false, showDuration = false }) => {
  if (session.empty) {
    return (
      <>
        <div className="black">
          <strong></strong>
        </div>
        <span className="currency"></span>
      </>
    )
  }

  return (
    <>
      {PROGRAM_LOGOS[session.category] && (
        <img
          className="harmonogram-desktop__logo"
          src={PROGRAM_LOGOS[session.category].src}
          alt=""
          aria-hidden="true"
        />
      )}
      {session.trainingType && (
        <span className="harmonogram-training-type">{session.trainingType}</span>
      )}
      <div className="black">
        <strong>
          {session.title}
          {session.registered && <sup>®</sup>}
          {session.subtitle && (
            <>
              <br />
              {session.subtitle}
            </>
          )}
          {session.age && (
            <>
              <br />
              <small>({session.age})</small>
            </>
          )}
        </strong>
      </div>
      {!hideTime && (
        <span className="currency">
          {session.time}
          {showDuration && (
            <small className="harmonogram-duration">{getSessionTiming(session).duration} min</small>
          )}
        </span>
      )}
    </>
  )
}

const HarmonogramComponent = (props) => {
  const { t } = useTranslation('translation')
  const categories = props.category || []
  const [activeDayIndex, setActiveDayIndex] = useState(currentWeekdayIndex)
  const summerScheduleActive = isSummerScheduleActive()

  const scheduleDays = useMemo(() => getScheduleDays(t('page.womenempowered')), [t])

  const isVisibleCategory = (session) => categories.includes(session.category)
  const isMasterCycleSchedule = categories.length === 1 && categories[0] === 'adults-pro'

  const visibleSessionsForDay = (day) => {
    if (!isMasterCycleSchedule) {
      return day.sessions.filter(isVisibleCategory)
    }

    const reflexDevelopmentSession = day.sessions
      .flatMap((session) => session.splitSessions || [session])
      .find((session) => session.subtitle === 'Reflex development')
    const masterCycleSessions = day.sessions.filter((session) => session.category === 'adults-pro')
    const missingMasterCycleSlots = Array.from(
      { length: Math.max(0, 2 - masterCycleSessions.length) },
      () => emptySlot('adults-pro'),
    )

    return [
      reflexDevelopmentSession || emptySlot('adults-pro'),
      ...masterCycleSessions,
      ...missingMasterCycleSlots,
    ]
  }

  const activeDay = scheduleDays[activeDayIndex]
  const activeDaySessions = visibleSessionsForDay(activeDay).reduce((sessions, session) => {
    if (session.empty) {
      return sessions
    }

    if (session.splitSessions) {
      return [...sessions, ...session.splitSessions.filter(isVisibleCategory)]
    }

    return [...sessions, session]
  }, [])

  const timelineDays = scheduleDays.map((day) => ({
    ...day,
    timelineSessions: visibleSessionsForDay(day).filter(
      (session) => !session.empty && getSessionTiming(session),
    ),
  }))
  const visibleTimelineSegments = TIMELINE_SEGMENTS.filter((segment) =>
    timelineDays.some((day) =>
      day.timelineSessions.some((session) => {
        const timing = getSessionTiming(session)

        return timing.start >= segment.start && timing.start < segment.end
      }),
    ),
  )

  const goToPreviousDay = () => {
    setActiveDayIndex((current) => (current === 0 ? scheduleDays.length - 1 : current - 1))
  }

  const goToNextDay = () => {
    setActiveDayIndex((current) => (current === scheduleDays.length - 1 ? 0 : current + 1))
  }

  return (
    <div>
      <section id="harmonogram" className="hg_section pt-80 pb-80">
        <div className="container">
          <div className="row">
            <div className="col-sm-12 col-md-12">
              <div className="kl-title-block text-center">
                <h3 className="tbk__title fs-xxl fw-thin">
                  <span className="fw-semibold fs-xxxl fs-xs-xl tcolor">Harmonogram</span> tréningov
                </h3>
              </div>
            </div>

            <div className="col-sm-12 col-md-12">
              {summerScheduleActive && (
                <div className="harmonogram-summer-notice">
                  <strong>Letný harmonogram od 25. júna do 20. augusta</strong>
                  <span>
                    Detské skupiny Malí šampióni, Mladí zápasníci a Black Belt Club majú počas leta
                    tréningovú pauzu. Ostatné tréningy prebiehajú podľa rozvrhu nižšie.
                  </span>
                </div>
              )}

              <div className="harmonogram-desktop-scroll">
                <div
                  className="harmonogram-desktop harmonogram-timeline"
                  aria-label="Časová os tréningov"
                >
                  <div className="harmonogram-timeline__header">
                    <div className="harmonogram-timeline__corner">Čas</div>
                    {timelineDays.map((day) => (
                      <div className="harmonogram-timeline__day-name" key={day.name}>
                        {day.name}
                      </div>
                    ))}
                  </div>

                  {visibleTimelineSegments.map((segment) => {
                    const segmentHeight = (segment.end - segment.start) * TIMELINE_PIXELS_PER_MINUTE

                    return (
                      <div className="harmonogram-timeline__section" key={segment.key}>
                        <div className="harmonogram-timeline__section-name">{segment.label}</div>
                        <div className="harmonogram-timeline__segment">
                          <div
                            className="harmonogram-timeline__axis"
                            style={{ height: `${segmentHeight}px` }}
                          >
                            {getTimelineMarks(segment).map((minutes, index, marks) => (
                              <span
                                className={`harmonogram-timeline__mark${
                                  index === 0 ? ' is-first' : ''
                                }${
                                  index === marks.length - 1 ? ' is-last' : ''
                                }${minutes % 60 === 0 ? ' is-hour' : ''}`}
                                style={{
                                  top: `${
                                    (minutes - segment.start) * TIMELINE_PIXELS_PER_MINUTE
                                  }px`,
                                }}
                                key={minutes}
                              >
                                {minutesToClockTime(minutes)}
                              </span>
                            ))}
                          </div>

                          <div className="harmonogram-timeline__days">
                            {timelineDays.map((day) => (
                              <div
                                className="harmonogram-timeline__track"
                                style={{
                                  height: `${segmentHeight}px`,
                                  '--timeline-step-height': `${
                                    TIMELINE_STEP_MINUTES * TIMELINE_PIXELS_PER_MINUTE
                                  }px`,
                                }}
                                key={`${segment.key}-${day.name}`}
                              >
                                {day.timelineSessions.map((session, index) => {
                                  const timing = getSessionTiming(session)

                                  if (timing.start < segment.start || timing.start >= segment.end) {
                                    return null
                                  }

                                  return (
                                    <article
                                      className={`harmonogram-timeline__session${
                                        timing.duration <= 30 ? ' is-short' : ''
                                      }${
                                        summerScheduleActive && isSummerPausedSession(session)
                                          ? ' is-summer-paused'
                                          : ''
                                      }${isUpcomingSession(session) ? ' is-upcoming' : ''}`}
                                      style={{
                                        top: `${
                                          (timing.start - segment.start) *
                                          TIMELINE_PIXELS_PER_MINUTE
                                        }px`,
                                        height: `${timing.duration * TIMELINE_PIXELS_PER_MINUTE}px`,
                                        backgroundColor: session.splitSessions
                                          ? undefined
                                          : session.color,
                                      }}
                                      key={`${day.name}-${segment.key}-${index}`}
                                    >
                                      {session.splitSessions ? (
                                        <div className="split-sessions">
                                          {session.splitSessions.map((splitSession) => (
                                            <div
                                              className={`split-sessions__item${
                                                isUpcomingSession(splitSession)
                                                  ? ' is-upcoming'
                                                  : ''
                                              }`}
                                              style={{
                                                backgroundColor: splitSession.color,
                                              }}
                                              key={splitSession.subtitle}
                                            >
                                              <SessionContent session={splitSession} hideTime />
                                            </div>
                                          ))}
                                          <span className="currency split-sessions__time">
                                            {session.splitSessions[0].time}
                                            <small className="harmonogram-duration">
                                              {timing.duration} min
                                            </small>
                                          </span>
                                        </div>
                                      ) : (
                                        <SessionContent session={session} showDuration />
                                      )}
                                    </article>
                                  )
                                })}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="harmonogram-mobile" aria-label="Mobilný harmonogram tréningov">
                <div className="harmonogram-mobile__nav">
                  <button type="button" onClick={goToPreviousDay} aria-label="Predchádzajúci deň">
                    ‹
                  </button>
                  <div>
                    <span>Deň</span>
                    <strong>{activeDay.name}</strong>
                  </div>
                  <button type="button" onClick={goToNextDay} aria-label="Nasledujúci deň">
                    ›
                  </button>
                </div>

                <div className="harmonogram-mobile__tabs" role="tablist" aria-label="Dni v týždni">
                  {scheduleDays.map((day, index) => (
                    <button
                      type="button"
                      className={index === activeDayIndex ? 'is-active' : ''}
                      onClick={() => setActiveDayIndex(index)}
                      key={day.name}
                    >
                      {day.shortName}
                    </button>
                  ))}
                </div>

                <div className="harmonogram-mobile__day">
                  {activeDaySessions.length > 0 ? (
                    activeDaySessions.map((session, index) => (
                      <article
                        className={`harmonogram-mobile__session${
                          summerScheduleActive && isSummerPausedSession(session)
                            ? ' is-summer-paused'
                            : ''
                        }${isUpcomingSession(session) ? ' is-upcoming' : ''}`}
                        style={{ borderTopColor: session.color }}
                        key={`${activeDay.name}-mobile-${index}`}
                      >
                        <div className="harmonogram-mobile__time">
                          <span>{session.time}</span>
                          <small>{getSessionTiming(session).duration} min</small>
                        </div>
                        <div className="harmonogram-mobile__program">
                          {PROGRAM_LOGOS[session.category] && (
                            <img
                              className="harmonogram-mobile__logo"
                              src={PROGRAM_LOGOS[session.category].src}
                              alt={PROGRAM_LOGOS[session.category].alt}
                            />
                          )}
                          {session.trainingType && (
                            <span className="harmonogram-training-type">
                              {session.trainingType}
                            </span>
                          )}
                          <strong>
                            {session.title}
                            {session.registered && <sup>®</sup>}
                          </strong>
                          {session.subtitle && <span>{session.subtitle}</span>}
                          {session.age && <small>({session.age})</small>}
                        </div>
                      </article>
                    ))
                  ) : (
                    <div className="harmonogram-mobile__empty">
                      V tento deň nie je pre vybraný program tréning.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default HarmonogramComponent
